import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, Users, Layers, HeartHandshake, Phone, BadgeDollarSign } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { Reveal, Chapter } from "../components/Reveal";
import { IMAGES } from "../lib/site";
import { useContent } from "../lib/content";

const IMG_WEDDING = "/api/files/glambooth-cayman/uploads/eb603645-290b-44a8-a5bc-0e4f59b372e3.jpeg";

const STATS = [
  { value: "2+", label: "Years of Experience" },
  { value: "4", label: "Media Formats" },
];

const PILLARS = [
  {
    icon: Layers,
    title: "Stylish Backdrops",
    text: "A variety of stylish backdrops to choose from, each designed to enhance the theme and ambiance of your event.",
  },
  {
    icon: Camera,
    title: "Latest Technology",
    text: "Booths equipped with the latest tech for high-quality photos, GIFs, Boomerangs, and videos — plus templates and overlays to personalize your event.",
  },
  {
    icon: Users,
    title: "White-Glove Team",
    text: "Professional, reliable, and friendly service. From setup to takedown, we handle every detail so you can focus on enjoying your event.",
  },
  {
    icon: BadgeDollarSign,
    title: "Competitive Pricing",
    text: "Memorable moments shouldn't come with a hefty price tag. Affordable packages without compromising on quality or experience.",
  },
];

export default function AboutPage() {
  const { settings } = useContent();
  const phone = settings?.phone || "+1 (345) 328-6172";
  const bizEmail = settings?.business_email || "info@glamboothcayman.com";
  return (
    <div data-testid="about-page">
      <Navbar />

      <header className="bg-[#3c003a] relative grain pt-40 pb-20 overflow-hidden">
        <img
          src={IMAGES.leaves}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3c003a]/60 to-[#3c003a]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="about-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#ffc4e4] text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] mb-5"
          >
            About <em className="text-[#c9006f]">GlamBooth</em> Cayman
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9 }}
            className="text-white/60 text-base md:text-lg max-w-2xl leading-[1.7] mb-12"
          >
            Your premier photo booth service provider, dedicated to adding an
            extra layer of fun and excitement to your events — weddings,
            corporate events, birthday parties, and every special occasion in
            between.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9 }}
            className="flex flex-wrap gap-4"
            data-testid="about-stats"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-white/[0.07] border border-white/15 backdrop-blur-md px-7 py-5"
              >
                <p className="font-serif text-[#ffc4e4] text-3xl">{s.value}</p>
                <p className="text-white/55 text-xs uppercase tracking-[0.2em] font-semibold mt-1">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </header>

      <section className="py-24 lg:py-32 bg-[#fafaf7]" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Chapter number="01" label="Who We Are" />
            <Reveal>
              <h2 className="font-serif text-[#121212] text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-6">
                Making every event <em className="text-[#c9006f]">glamorous</em>{" "}
                <em className="text-[#91008d]">& fun</em>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#121212]/65 text-base md:text-lg leading-[1.8] mb-5">
                Welcome to GlamBooth Cayman. Whether it's a wedding, corporate
                event, birthday party, or any special occasion, we are here to
                ensure that your guests have an unforgettable experience.
              </p>
              <p className="text-[#121212]/65 text-base md:text-lg leading-[1.8]">
                We understand that every event is unique, and we go above and
                beyond to tailor our services to meet your specific needs and
                exceed your expectations — creating lasting memories with
                top-notch photo booth services.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="glow-card rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(145,0,141,0.2)]">
                <img src={IMG_WEDDING} alt="Newlyweds at a GlamBooth" className="w-full h-[480px] object-cover" />
              </div>
              <div
                className="absolute -bottom-6 -left-6 hidden sm:block bg-white rounded-2xl shadow-[0_16px_50px_rgba(145,0,141,0.18)] px-6 py-5"
              >
                <p className="font-serif italic text-[#91008d] text-lg">Trusted island-wide</p>
                <p className="text-[#121212]/50 text-sm">GlamBooth & Raw Photo Booth</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-24 lg:py-32 bg-[#f8eef6]" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Chapter number="02" label="What Sets Us Apart" />
          <Reveal>
            <h2 className="font-serif text-[#121212] text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-16">
              Service that <em className="text-[#a3005a]">exceeds expectations</em>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.35 }}
                    className="h-full rounded-3xl bg-white border border-black/5 p-8 shadow-[0_8px_30px_rgba(145,0,141,0.06)]"
                    data-testid={`about-pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#91008d]/10 text-[#91008d] mb-6">
                      <Icon size={22} strokeWidth={1.6} />
                    </span>
                    <h3 className="font-serif text-[#121212] text-2xl mb-3">{p.title}</h3>
                    <p className="text-[#121212]/60 leading-[1.7] text-sm md:text-base">{p.text}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28 bg-[#3c003a] relative grain overflow-hidden" data-testid="about-contact-strip">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-4 mb-14">
            <Link
              to="/"
              state={{ scrollTo: "#contact" }}
              data-testid="about-cta-book-btn"
              className="btn-gold inline-block rounded-full px-9 py-4 text-base font-bold tracking-wide"
            >
              Book Your Date
            </Link>
            <Link
              to="/gallery"
              data-testid="about-cta-gallery-btn"
              className="inline-block rounded-full px-9 py-4 text-base font-semibold text-white border border-white/30 hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
            >
              See Our Work
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 text-white/50 text-sm">
            <span className="inline-flex items-center gap-2">
              <HeartHandshake size={16} className="text-[#c9006f]" /> {bizEmail}
            </span>
            <span className="hidden sm:block text-white/20">•</span>
            <span className="inline-flex items-center gap-2">
              <Phone size={16} className="text-[#c9006f]" /> {phone}
            </span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
